import { Component, OnDestroy, ViewEncapsulation } from "@angular/core";
import {
  SendMessageEvent,
  ConversationalUIModule,
  ExecuteActionEvent,
} from "@progress/kendo-angular-conversational-ui";
import { CommonModule } from "@angular/common";
import { FloatingActionButtonModule } from "@progress/kendo-angular-buttons";
import {
  PopoverAnchorDirective,
  PopoverModule,
} from "@progress/kendo-angular-tooltip";
import { ChatBotStore } from "./chat-bot.store";
import { Router } from "@angular/router";
import { ActionTypes, Prompt, FeedbackFormValue, Link } from "./interfaces";
import { AvatarModule } from "@progress/kendo-angular-layout";
import { FeedbackFormComponent } from "./feedback/feedback-form/feedback-form.component";
import { PhoenixButtonModule } from "@kehe/phoenix-button";
import { BreakpointService } from "@kehe/phoenix-utils";
import { PhoenixModalComponent } from "@kehe/phoenix-modal";

@Component({
  selector: "app-chat-bot",
  imports: [
    CommonModule,
    ConversationalUIModule,
    FloatingActionButtonModule,
    PopoverModule,
    AvatarModule,
    FeedbackFormComponent,
    PhoenixButtonModule,
    PhoenixModalComponent,
  ],
  providers: [ChatBotStore],
  templateUrl: "./chat-bot.component.html",
  encapsulation: ViewEncapsulation.None,
  styleUrl: "./chat-bot.component.scss",
})
export class ChatBotComponent implements OnDestroy {
  viewModel$ = this.chatBotStore.selectViewModel$;
  hasSuggestedActionBeenClicked = false;
  isMobileView = false;
  modalOpen = false;

  sub = this.breakpointService.isSmallAndBelow$.subscribe(
    (val) => (this.isMobileView = val),
  );

  constructor(
    private readonly chatBotStore: ChatBotStore,
    private readonly breakpointService: BreakpointService,
    private router: Router,
  ) {
    this.chatBotStore.getPrompts();
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  addUserMessage(e: SendMessageEvent): void {
    this.chatBotStore.addUserMessage(e.message);
  }

  onPopoverShown(): void {
    this.chatBotStore.patchState({ isChatOpen: true });
  }

  onPopoverHidden(): void {
    this.chatBotStore.patchState({ isChatOpen: false });
  }

  toggle(anchor?: PopoverAnchorDirective): void {
    // toggle popover if anchor is passed in
    if (anchor && !this.isMobileView) {
      anchor.toggle();
    }
    // only open modal if mobileView is true
    // modal might still be open even if mobileView is false, so always close if open already
    // need to call shown / hidden function to make sure states match
    // popover will call these funcs on its own
    if (this.modalOpen) {
      this.modalOpen = false;
      this.onPopoverHidden();
    } else if (!this.modalOpen && this.isMobileView) {
      this.modalOpen = true;
      this.onPopoverShown();
    }
  }

  onCustomSuggestedAction(e: ExecuteActionEvent): void {
    this.hasSuggestedActionBeenClicked = true;
    switch (e.action.type) {
      case ActionTypes.Prompt:
        this.chatBotStore.processPrompt(e.action as unknown as Prompt);
        break;
      case ActionTypes.MoreHelp:
        this.chatBotStore.addMoreHelpResponseFromChatBot(true);
        break;
      case ActionTypes.NoMoreHelp:
        this.chatBotStore.addMoreHelpResponseFromChatBot(false);
        break;
      case ActionTypes.IDontKnow:
        this.chatBotStore.userDoesNotKnowUpcOrEsn();
        break;
      default:
        break;
    }
  }

  onResponseLinkClick(link: Link): void {
    if (link.isExternal) {
      window.open(link.url, "_blank");
    } else {
      this.router.navigateByUrl(link.url);
    }
  }

  providePositiveFeedback(activeSystemMessageId: string): void {
    this.chatBotStore.patchState({ activeSystemMessageId });
    this.chatBotStore.sendFeedback(true);
  }

  provideNegativeFeedback(activeSystemMessageId: string): void {
    this.chatBotStore.patchState({
      activeSystemMessageId,
      isFeedbackFormOpen: true,
    });
  }

  cancelFeedbackForm(): void {
    this.chatBotStore.patchState({ isFeedbackFormOpen: false });
  }

  submitFeedbackForm(feedbackFormValue: FeedbackFormValue): void {
    this.chatBotStore.patchState({ feedbackFormValue });
    this.chatBotStore.sendFeedback(false);
  }

  onChatTextboxFocus(event): void {
    if (
      this.hasSuggestedActionBeenClicked &&
      event.target.offsetParent.classList.contains("k-message-box")
    ) {
      event.target.blur();
      this.hasSuggestedActionBeenClicked = false;
    }
  }
}
